import React from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { useSubscription } from '../../contexts/SubscriptionContext'
import { useAuth } from '../../contexts/AuthContext'

interface TrialGuardProps {
  children?: React.ReactNode
  fallback?: React.ReactNode
  redirectTo?: string
}

/**
 * TrialGuard HOC - Protege rotas que exigem subscription ativa ou trial
 *
 * Uso:
 * <TrialGuard>
 *   <ProtectedComponent />
 * </TrialGuard>
 *
 * Ou como layout route (protege todas as rotas filhas):
 * <Route element={<TrialGuard />}>
 *   <Route path="editor" element={<AssessmentEditor />} />
 * </Route>
 */
const TrialGuard: React.FC<TrialGuardProps> = ({
  children,
  fallback,
  redirectTo = '/app/billing'
}) => {
  const { isActive, isTrialing, loading } = useSubscription()
  const { user, isAdmin, loading: authLoading, trialActivating } = useAuth()

  // Wait for auth and subscription to load — e pelo auto-trial de um cadastro
  // recém-feito: até ele terminar, "sem assinatura" ainda não quer dizer "sem
  // direito" (AuthContext encerra a espera em no máximo 10s).
  if (loading || authLoading || trialActivating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-terra-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-ink-500 dark:text-ink-300">Verificando acesso...</p>
        </div>
      </div>
    )
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Admins have full access without restrictions
  if (isAdmin) {
    return <>{children ?? <Outlet />}</>
  }

  // Has active subscription or trial
  if (isActive || isTrialing) {
    return <>{children ?? <Outlet />}</>
  }

  // No active subscription - show fallback or redirect
  if (fallback) {
    return <>{fallback}</>
  }

  return <Navigate to={redirectTo} replace />
}

export default TrialGuard
